import type { RoofingServiceContent } from "@/components/RoofingServicePage";

export const solarServicePages: Record<string, RoofingServiceContent> = {
  "solar-installation": {
    type: "solar",
    currentSlug: "solar-installation",
    heroTitle: "Solar Installation in Southern California",
    heroSubtitle: "Custom-designed solar systems built around your roof, your energy use and your long-term goals – installed by a team that handles the roof first.",
    intro: "Solar only works long-term when the roof underneath it is right, the panels are sized to your actual usage, and the install is clean. That's how we approach every McKay solar project in Southern California.",
    sections: [
      {
        title: "When solar installation is the right move",
        body: [
          "Most Southern California homeowners look at solar for the same reasons – rising utility bills, a roof that gets great sun, and wanting some control over energy costs going forward. If your roof still has plenty of life left and your usage is steady, solar usually makes sense.",
          "We install across Southern California, Coronado, Winter Gardens, Pine Valley, Poway, Carlsbad and the surrounding cities. Every home is different, so we size the system around what your household actually uses, not a one-size template.",
          "Under NEM 3.0, the math has shifted from previous years – exports are worth less than they used to be, so systems that lean on daytime self-consumption and evening battery use tend to pay back faster than ones sized purely to offset the annual bill on paper. We walk through your actual utility usage history before we size anything.",
        ],
      },
      {
        title: "What our solar installation includes",
        body: [
          "A full install covers system design, permits, panels, inverters, racking, wiring, monitoring and utility interconnection. We handle the paperwork with the city and your utility so you're not chasing anything.",
          "Before anything gets mounted, we look at the roof. If it needs repair or is close to replacement, we tell you upfront – putting panels on a failing roof is the fastest way to pay twice.",
          "Roof type changes the install plan. Concrete and clay tile need tile hooks and lifted mounts so we're not cracking tile to get a flush fit; composition shingle takes standard flashed lag mounts; flat and low-slope sections get ballasted or fully-adhered racking depending on the membrane. We choose the attachment method for the roof in front of us, not a default kit.",
        ],
      },
      {
        title: "How long a solar install takes",
        body: [
          "Most residential installs run one to three days on-site once the crew is out. The bigger timeline is permitting and utility approval, which varies by city.",
          "We give you a realistic schedule up front and keep you posted at each step – design, permits, install, inspection and permission to operate.",
        ],
      },
      {
        title: "Panels, inverters and batteries built for the long haul",
        body: [
          "We use tier-1 panels and inverters with strong performance warranties. Micro-inverters or string inverters get chosen based on your roof layout and shading, not what's convenient for us to install.",
          "If backup power matters to you, we can pair the system with battery storage – including Tesla Powerwall – so you keep the lights on when the grid goes down.",
          "Panel degradation is a real number, not a marketing footnote – expect roughly 0.3–0.5% output loss per year on a quality panel, which is why we size systems with a little headroom instead of exactly matching today's bill. Twenty years from now the array should still be doing real work.",
        ],
      },
      {
        title: "Why the roof matters as much as the panels",
        body: [
          "Because we do roofing too, we plan every install so it protects the roof underneath. Flashings, penetrations and attachment points are done the way a roofer would want them done – not just the way an electrician would.",
          "That means fewer callbacks, no mystery leaks a year later, and a system that's actually easy to service if a panel ever needs to come off.",
        ],
      },
      {
        title: "Coastal soiling and system placement",
        body: [
          "Homes near the coast in Pacific Beach, Point Loma and Coronado deal with marine-layer haze and salt film settling on panels far more than inland homes do. That soiling can quietly cut production 5–10% between rinses if it's never addressed, so we factor cleaning frequency into what we tell you to expect from year-round output.",
          "Panel orientation and row spacing also get planned around your specific roof geometry and any shading from chimneys, trees or neighboring structures – a slightly different tilt or layout can be the difference between a system that hits its production estimate and one that quietly underperforms all year.",
        ],
      },
      {
        title: "Permitting and HOA approval in Southern California",
        body: [
          "Every jurisdiction we work in – the City of Southern California, the County, Coronado, Winter Gardens, Poway and the rest – has its own permitting portal and its own quirks in what plan sets they want. We prepare the structural and electrical drawings up front so the application doesn't bounce back with corrections.",
          "If your neighborhood has an HOA with design guidelines around panel visibility or roof-mounted equipment, we put together what's needed for sign-off before the crew is ever scheduled, so approval isn't the thing holding up your install.",
        ],
      },
    ],
  },

  "solar-panel-repair": {
    type: "solar",
    currentSlug: "solar-panel-repair",
    heroTitle: "Solar Panel Repair in Southern California",
    heroSubtitle: "Underperforming array? We diagnose and repair panels, wiring, inverters and roof penetrations so your system actually produces what it should.",
    intro: "When production drops or an inverter starts throwing errors, the fix is usually smaller than people expect. We find the actual problem, repair it, and get the system back to full output.",
    sections: [
      {
        title: "Signs your solar system needs repair",
        body: [
          "Lower monthly production, error codes on the inverter, a panel that's clearly darker than the others, or a jump in your utility bill are the most common red flags. Sometimes it's one loose connection. Sometimes it's a failed micro-inverter or a damaged panel.",
          "We check monitoring data, physically inspect the array, and confirm exactly what's off before recommending anything.",
          "A quieter sign is a bill that looks normal on paper but doesn't match what the system was projected to produce two or three years ago. Comparing your monitoring app's year-over-year output against the original design estimate usually surfaces a problem well before it shows up as a visible fault.",
        ],
      },
      {
        title: "What solar repair usually involves",
        body: [
          "Repairs commonly include replacing failed micro-inverters or optimizers, fixing wiring and MC4 connectors, replacing damaged panels, or resealing roof penetrations that have started to leak.",
          "If the issue is on the roofing side – flashings, boots or underlayment – we handle that in the same visit instead of sending you to a separate contractor.",
          "On tile roofs specifically, cracked or slipped tile around a mounting foot is one of the more common repair calls we get. We reset or replace the tile and re-flash the penetration properly instead of just caulking over the gap, which is a short-term fix that fails again in a season or two.",
        ],
      },
      {
        title: "How long a repair takes",
        body: [
          "Most solar repairs are same-day once we're on the roof and have the parts. Inverter or panel replacements can take a bit longer depending on availability.",
          "We give you a straight answer on cost and timing before any work starts.",
        ],
      },
      {
        title: "Repairing systems we didn't install",
        body: [
          "A lot of the repair calls we get are on systems installed by companies that are no longer around or won't come back out. We service those too.",
          "We'll document what we find, explain your options honestly, and only recommend replacement when repair really isn't the smart move.",
        ],
      },
      {
        title: "Marine-layer soiling vs. an actual fault",
        body: [
          "Before we call anything a hardware failure, we rule out the boring explanation first: dirty panels. Coastal soiling and dust buildup after a dry inland stretch can drag production down 5–10% and it looks a lot like a wiring or inverter issue on a monitoring graph until the array gets rinsed.",
          "If a clean doesn't bring output back up, that confirms it's a real electrical or hardware problem, and we move straight into targeted diagnostics instead of guessing.",
        ],
      },
      {
        title: "Repairs after storms and Santa Ana wind events",
        body: [
          "Santa Ana winds can loosen racking bolts, lift a panel edge, or snap a connector that's been baking and flexing in the sun for years. After a big wind event we check torque on racking hardware and inspect for any panel that's shifted out of alignment, since a panel sitting slightly off can chafe wiring against the roof or racking over time.",
          "We also check roof penetrations after any major storm – wind-driven rain finds a weak flashing seal fast, and it's easier to reseal it now than to chase a stain on the ceiling six months later.",
        ],
      },
    ],
  },

  "solar-panel-replacement": {
    type: "solar",
    currentSlug: "solar-panel-replacement",
    heroTitle: "Solar Panel Replacement in Southern California",
    heroSubtitle: "Upgrade older or damaged arrays to modern high-efficiency panels – often with better warranties, higher output and cleaner roof integration.",
    intro: "Older solar systems still work, but many are producing well below what modern panels can deliver on the same roof. Replacement makes sense when the numbers stop adding up or the hardware is aging out.",
    sections: [
      {
        title: "When panel replacement is worth it",
        body: [
          "If your system is 12–15+ years old, has failing inverters, damaged panels or is producing noticeably less than it used to, replacement is usually worth pricing out. Newer panels squeeze more energy out of the same roof space.",
          "It's also common to replace older systems when you're upgrading the roof itself – doing both at once is cheaper than doing them a year apart.",
          "Panels bought before around 2015 are also a full generation behind on efficiency, which matters more now under NEM 3.0 since maximizing daytime self-consumption is worth more than it used to be. Swapping a 15%-efficient legacy panel for a modern 21–22% panel can meaningfully shrink the footprint needed to cover the same usage.",
        ],
      },
      {
        title: "What replacement includes",
        body: [
          "We remove the existing panels, racking and wiring, inspect the roof underneath, and address any issues before the new system goes back up. New racking, panels, inverters and monitoring get installed with fresh flashings and sealed penetrations.",
          "We handle permits and utility re-interconnection so the new system gets approved to operate the right way.",
          "Because we're a licensed roofing crew as well, tile and shingle repair under the old mounting points happens as part of the same visit – no separate contractor, no gap between removal and re-install where the roof sits exposed.",
        ],
      },
      {
        title: "Pairing replacement with a new roof",
        body: [
          "If your roof is close to end-of-life, replacing panels without addressing the roof usually means paying to detach and re-set the array again in a few years. We can bundle the roof and solar together so it's done cleanly, once.",
          "This is the most cost-effective time to fix roof issues, since the array is already off and the roof surface is fully exposed – there's no premium for working around panels that are staying put.",
        ],
      },
      {
        title: "Adding battery storage during replacement",
        body: [
          "Replacement is a natural time to add batteries. New system design, new inverter, and battery integration can all be planned together for a cleaner install and better long-term performance.",
        ],
      },
      {
        title: "NEM interconnection changes during replacement",
        body: [
          "Swapping panels or expanding a system can trigger a new interconnection review with your utility, and depending on the scope it may move your account onto NEM 3.0 rules if you're not already on them. We walk through what that means for your specific bill before we finalize a design, so there are no surprises on the export credit rate after the switch flips on.",
        ],
      },
      {
        title: "What to do with old panels",
        body: [
          "Panels removed during a replacement still have real value in some cases – functioning older panels can sometimes be repurposed for a shed, detached garage or well-shaded secondary structure where peak efficiency doesn't matter as much. Where reuse doesn't make sense, we handle responsible recycling instead of letting them pile up in a garage.",
        ],
      },
    ],
  },

  "solar-battery-installation": {
    type: "solar",
    currentSlug: "solar-battery-installation",
    heroTitle: "Solar Battery Installation in Southern California",
    heroSubtitle: "Home battery backup so you keep power when the grid goes down – and get more value out of every kilowatt your solar produces.",
    intro: "With Southern California utility rates and NEM 3.0, storing your own solar energy is more valuable than sending it back to the grid. Batteries give you backup power and a smarter way to use what your panels make.",
    sections: [
      {
        title: "Why batteries make sense in Southern California",
        body: [
          "Under NEM 3.0, exporting solar to the grid pays a lot less than it used to. Batteries let you use your own solar in the evening instead of buying expensive peak-rate power back from the grid.",
          "They also cover you during outages. Public safety power shutoffs, storms and grid events are a real thing – a battery keeps essentials running.",
          "Time-of-use rate structures make the math even more favorable for storage – the priciest hours line up almost exactly with the evening window when solar production has already tapered off. A battery lets you shift the power you generated at noon into the 4–9pm rate spike instead of paying peak pricing for it.",
        ],
      },
      {
        title: "What battery installation includes",
        body: [
          "We size the battery based on your usage, existing solar and what you actually want backed up. From there we handle design, permits, install, inverter integration and utility approval.",
          "Placement matters – batteries need proper ventilation, clearance and mounting. We plan the location around your garage, side yard or exterior wall so it's clean and code-compliant.",
        ],
      },
      {
        title: "Pairing batteries with existing solar",
        body: [
          "We add batteries to systems we didn't originally install all the time. In most cases we can integrate storage without replacing your existing panels or inverter, depending on what you already have.",
        ],
      },
      {
        title: "How long a battery install takes",
        body: [
          "Most residential battery installs take one to two days on-site. Timing depends on how many units, whether the existing electrical panel needs upgrades, and utility approval.",
        ],
      },
      {
        title: "Sizing a battery around your actual usage",
        body: [
          "There's no universal battery size that fits every home. We look at your evening and overnight load – appliances, HVAC, any EV charging – against what your solar array actually produces in the shoulder seasons when days are shorter, and size around that instead of the sunniest day in July.",
          "Homes that want whole-home backup during an outage need a bigger system than homes that just want the fridge, internet and a few circuits covered. We lay out both options with real numbers so you're choosing based on cost and need, not guesswork.",
        ],
      },
      {
        title: "Battery installation and roof-mounted solar interaction",
        body: [
          "Because the battery ties into the existing solar circuit and main panel, we double-check the condition of conduit runs, roof penetrations and existing wiring while we're up there rather than treating the battery as an isolated add-on. Catching a marginal connection now is a lot cheaper than a service call after the battery's been drawing on it for a year.",
        ],
      },
    ],
  },

  "tesla-powerwall-installation": {
    type: "solar",
    currentSlug: "tesla-powerwall-installation",
    heroTitle: "Tesla Powerwall Installation in Southern California",
    heroSubtitle: "Certified Powerwall installs, paired cleanly with your solar so your home has real backup power and smarter energy use.",
    intro: "Powerwall is one of the strongest home battery options on the market. We install it the right way – sized for your usage, integrated with your solar and set up for backup that actually works when the grid goes down.",
    sections: [
      {
        title: "What Powerwall does for a Southern California home",
        body: [
          "Powerwall stores your solar energy so you can use it after sunset instead of buying power back at peak rates. During outages, it automatically keeps critical circuits running – lights, fridge, internet, medical equipment – without you doing anything.",
          "It works with new solar installs and with existing systems, and multiple Powerwalls can be stacked for larger homes or whole-home backup.",
          "Under NEM 3.0, running Powerwall in self-consumption mode instead of exporting to the grid is usually the better financial move for most households, since export credits are worth a fraction of what they paid a few years ago. We help configure the Tesla app settings so the battery is actually optimized for your rate plan, not left on a generic default.",
        ],
      },
      {
        title: "What our Powerwall install includes",
        body: [
          "Site assessment, sizing, permits, mounting, wiring, gateway setup, utility interconnection and app configuration. We handle the whole thing so it works end-to-end.",
          "We plan the location – garage wall, exterior side yard – around code clearances and how the space is actually used.",
        ],
      },
      {
        title: "Adding Powerwall to existing solar",
        body: [
          "In most cases we can add Powerwall to an existing solar system without ripping anything out. We check your current inverter, electrical panel and layout and lay out exactly what's involved.",
          "Some older inverter brands don't pair cleanly with Powerwall's gateway, in which case we'll tell you upfront whether an inverter swap is needed rather than discovering it mid-install.",
        ],
      },
      {
        title: "How long install takes",
        body: [
          "Most single-Powerwall installs are done in a day on-site once permits are in hand. Multi-unit installs or homes that need an electrical panel upgrade take a bit longer.",
        ],
      },
      {
        title: "Powerwall as certified backup during outages",
        body: [
          "Southern California's fire-season public safety power shutoffs and the occasional grid event make backup power a practical concern in the county's more exposed inland and canyon-adjacent neighborhoods, not just a nice-to-have. We configure Powerwall's backup gateway so the transition during an outage is automatic and seamless – no manual switching, no generator startup delay.",
          "We'll also walk through realistic backup duration for your specific home given your loads, so you know whether one unit covers what you need or whether a second unit makes sense.",
        ],
      },
      {
        title: "Maintenance and monitoring after install",
        body: [
          "Once it's running, Powerwall needs very little hands-on maintenance, but we make sure you know how to read the Tesla app – state of charge, backup reserve settings, and time-based control mode – so you can adjust it yourself as your rate plan or usage changes. We're also available if your utility updates rate structures again and it's worth revisiting your settings.",
        ],
      },
    ],
  },

  "solar-inverter-installation": {
    type: "solar",
    currentSlug: "solar-inverter-installation",
    heroTitle: "Solar Inverter Installation in Southern California",
    heroSubtitle: "String and micro-inverter installs and replacements to restore full system output – for new solar systems and aging arrays alike.",
    intro: "The inverter is the heart of a solar system. When it fails, production stops. When it's undersized or aging, you leave energy on the table. We install and replace inverters so your system actually delivers what it's supposed to.",
    sections: [
      {
        title: "Signs your inverter needs attention",
        body: [
          "Error codes on the display, flatlined production in your monitoring app, unusual noise or shutting down in the middle of the day are all signs an inverter is failing.",
          "Most string inverters last 10–15 years, and many original systems in Southern California are hitting that window right now – a lot of the systems installed during the county's early-2010s solar boom are due for an inverter swap regardless of anything else being wrong with the array.",
        ],
      },
      {
        title: "String vs micro-inverters",
        body: [
          "String inverters are simpler and cost less upfront but rely on the whole array working evenly. Micro-inverters or optimizers let each panel produce independently, which usually wins on complex roofs or shaded arrays.",
          "We recommend based on your roof and usage – not on whichever product we happen to install most.",
        ],
      },
      {
        title: "What inverter replacement includes",
        body: [
          "We remove the old inverter, inspect wiring and connections, install and configure the new unit, and verify system performance end-to-end. Utility notifications are handled where required.",
        ],
      },
      {
        title: "How long installation takes",
        body: [
          "Most inverter replacements are a same-day job. Full micro-inverter retrofits across a whole array take longer since each panel gets touched.",
        ],
      },
      {
        title: "Battery-ready and hybrid inverters",
        body: [
          "If battery storage is on your radar even if you're not ready today, it's worth installing a hybrid or battery-ready inverter now rather than paying to swap it again later. We'll flag when that upgrade makes sense based on your goals so you're not boxed into a second replacement in a year or two.",
        ],
      },
      {
        title: "Inverter monitoring and catching failures early",
        body: [
          "A properly configured monitoring setup will flag a dip in a specific string or panel well before it becomes a total inverter failure. Part of our install and replacement process is making sure your monitoring app and any manufacturer alerts are actually set up and reachable, so problems get caught in weeks, not discovered a year later on a surprise utility bill.",
        ],
      },
    ],
  },

  "ev-charger-installation": {
    type: "solar",
    currentSlug: "ev-charger-installation",
    heroTitle: "EV Charger Installation in Southern California",
    heroSubtitle: "Level 2 EV charger installs for homes and businesses – often bundled with a solar or battery upgrade so charging costs less to run.",
    intro: "A Level 2 charger cuts charge time from days to hours and pairs perfectly with solar. We handle the electrical, mounting and permits so your charger works safely and passes inspection on the first pass.",
    sections: [
      {
        title: "Why go Level 2",
        body: [
          "A standard 120V outlet barely keeps up with daily driving. A Level 2 charger gives you a full charge overnight and works with every major EV – Tesla, Ford, Rivian, Kia, Hyundai and the rest.",
          "Charging on solar power is significantly cheaper than peak utility rates, especially when paired with battery storage.",
          "Most utilities also offer EV-specific time-of-use rate plans with very low overnight pricing – timing a charger to start after peak hours, or running it directly off stored solar, is usually the cheapest way to fuel a car in the county by a wide margin.",
        ],
      },
      {
        title: "What the install includes",
        body: [
          "Load calculation, permit, breaker and panel work if needed, conduit run, charger mounting, wiring and final inspection. We route the wiring cleanly – no messy exposed conduit unless there's no other option.",
        ],
      },
      {
        title: "Panel upgrades and load management",
        body: [
          "Older homes sometimes don't have enough capacity for a Level 2 charger on the existing panel. We check that up front and give you options – smart load management devices or a panel upgrade – before we start.",
        ],
      },
      {
        title: "Bundling with solar or batteries",
        body: [
          "Installing a charger at the same time as new solar or a battery is almost always cheaper than doing them separately. We can lay out the full system so everything works together from day one.",
        ],
      },
      {
        title: "Choosing charger placement and equipment",
        body: [
          "Garage-mounted chargers are the most common setup, but plenty of Southern California homes need a driveway or side-yard run instead depending on where the car actually parks. We plan the conduit path to be as short and clean as possible, and we'll talk through hardwired versus plug-in units, since a hardwired charger is often a cleaner, more weatherproof choice for anything mounted outdoors.",
        ],
      },
      {
        title: "Permitting for EV chargers in Southern California",
        body: [
          "Most jurisdictions in the county treat a Level 2 charger install as an electrical permit, and several cities now offer expedited or over-the-counter review for standard residential EV charger permits. We know which cities move fast and which ones need more lead time, and we build that into your schedule instead of promising a date we can't hit.",
        ],
      },
    ],
  },

  "commercial-solar": {
    type: "solar",
    currentSlug: "commercial-solar",
    heroTitle: "Commercial Solar in Southern California",
    heroSubtitle: "Solar systems engineered for warehouses, offices, multi-unit properties and businesses – sized for real ROI and built for a working schedule.",
    intro: "Commercial solar isn't just a bigger residential system. Roof structure, electrical loads, tenant impact and payback timelines all get factored in. We design and install commercial systems that make financial sense and don't disrupt your operation.",
    sections: [
      {
        title: "Who commercial solar is for",
        body: [
          "Warehouses, offices, retail buildings, apartment complexes, agricultural properties and light industrial sites across Southern California. If you have roof area, high daytime energy use and a long-term interest in the property, solar usually makes sense.",
          "We work with owners, property managers and facilities teams and coordinate around occupancy and business hours.",
        ],
      },
      {
        title: "What commercial solar includes",
        body: [
          "Structural assessment, energy modeling, system design, permitting, utility coordination, installation, monitoring and commissioning. We handle utility interconnection and any AHJ requirements.",
          "Where it fits, we bring battery storage into the design so you can control demand charges and improve payback.",
        ],
      },
      {
        title: "Roof structure and roofing coordination",
        body: [
          "Because we're a roofing company too, we look at the roof first. If it needs work, we coordinate roof and solar together so you're not paying to remove and reinstall panels a few years later.",
          "Flat roofs, TPO, modified bitumen and metal roofs all get different attachment approaches. We use the right one for the roof – not a one-size-fits-all mount.",
        ],
      },
      {
        title: "Financing, incentives and ROI",
        body: [
          "We walk through federal incentives, depreciation and available financing options with straightforward numbers – not a sales pitch. If solar doesn't pencil out for your building, we'll tell you.",
        ],
      },
      {
        title: "Working around your operation",
        body: [
          "Commercial installs are scheduled around your business – after hours, weekends, in phases where needed. Safety plans, site access, tenant communication and cleanup all get planned up front so the project runs quietly in the background.",
        ],
      },
      {
        title: "Demand charge management for commercial accounts",
        body: [
          "Commercial utility rate structures often include demand charges based on peak usage spikes, which residential customers don't deal with at all. A well-sized battery paired with solar can shave those peaks and meaningfully cut a commercial bill even when total energy use doesn't change much – we model this specifically for your account's rate schedule rather than assuming a residential-style savings estimate applies.",
        ],
      },
      {
        title: "Multi-tenant and NEM aggregation considerations",
        body: [
          "Properties with multiple meters – apartment buildings, mixed-use retail, business parks – have options like NEM aggregation or virtual net metering that can spread solar production credit across several accounts on one property. It adds a layer of paperwork with your utility, but for the right property it makes solar pencil out where a single-meter system wouldn't. We'll tell you plainly whether your property is a good candidate before you spend time chasing it.",
        ],
      },
    ],
  },
};
